import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Card,
    CardMedia,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    IconButton,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import * as lodash from 'lodash';
import { MuiColorInput } from 'mui-color-input';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { IResponse } from '../../api/httpClient';
import * as MaterialHelper from '../../api/material/materialHelper';
import { equivalentThickness, equivalentThicknessIn, physicalData, relativeDensity, rockD50, rockD50in, shieldsParameter, thickness, thicknessIn } from '../../conversion/physicalData';
import { ELanguages, TLanguages, languages } from '../../localization';
import { route } from '../../route/Index';
import InputText from '../../shared/component/InputText';
import { EProducer, EStatus, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IMaterial, IPhysicalData, ITranslation } from '../../shared/iProject';
import variable from '../../shared/style/variable';

interface IMaterialDetailProps {}

const MaterialContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let translations: ITranslation[] = [];

let newPhysicalData: IPhysicalData = {
    rockD50: 0,
    rockD50in: 0,
    equivalentThickness: 0,
    equivalentThicknessIn: 0,
    thickness: 0,
    thicknessIn: 0,
    shieldsParameter: 0,
    relativeDensity: 0,
    depthParameterKh: 0,
    turbolenceParameterKt: 0,
    stabilityParameter: 0,
    widthOfTheProtectionW: 0,
    slopeParameter: 0,
};

let newMaterial: IMaterial = {
    type: 0,
    category: 1,
    producer: EProducer.Other,
    status: 1,
    color: 'red',
    isBaseMaterial: true,
    geotextileFilter: true,
    xties: true,
    physicalData: [newPhysicalData],
    translations: [
        {
            iso: 'en',
            name: 'New Material',
        },
    ],
};

const MaterialDetail = (props: IMaterialDetailProps) => {
    let { param } = useParams();

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [material, setMaterial] = React.useState<IMaterial | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [image, setImage] = React.useState<any[]>(['']);

    React.useEffect(() => {
        const fetchMaterial = async () => {
            setIsLoading(true);
            if (param === 'newMaterial') {
                setMaterial(newMaterial);
                setImage(['']);
                translations = lodash.cloneDeep(newMaterial.translations!);
            } else {
                let materialId = parseInt(param!);
                let material = await MaterialHelper.getMaterialByIdAsync(materialId);
                setMaterial(material.data);
                setImage(material.data.images!.map(x => x.imageUrl));
                translations = lodash.cloneDeep(material.data.translations!);
            }
            setIsLoading(false);
        };
        fetchMaterial();
    }, []);

    const handleClickSave = async () => {
        let payload: IMaterial = {
            ...material!,
            translations,
            images: undefined,
        };
        const formData = new FormData();
        formData.append('material', JSON.stringify(payload!));
        image?.forEach(x => {
            if (typeof x !== 'string') formData.append(`imageFiles`, x);
        });
        let res: IResponse<IMaterial>;
        if (param === 'newMaterial') {
            res = await MaterialHelper.insertMaterialWithImageAsync(formData!);
        } else {
            res = await MaterialHelper.updateMaterialWithImageAsync(formData!);
        }
        if (res.status == 200) {
            Swal.fire({
                title: t('savedGeometry'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    const handleChangeTraslation = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let index = translations.findIndex(x => x.iso === name);
        if (index !== -1) {
            translations[index].name = value;
        } else {
            translations.push({
                iso: name,
                name: value,
            });
        }
    };

    const handleClickRemoveTranslation = (iso: string) => (translations = translations.filter(x => x.iso !== iso));

    const handleImageChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        const file = event.target.files[0];
        let t = lodash.cloneDeep(image);
        t[index] = file;
        setImage(t);
    };

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value } = event.target;
        let t = lodash.cloneDeep(material);
        t!.physicalData[index] = {
            ...t!.physicalData[index],
            [name]: value,
        };
        setMaterial(t);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <ArrowForwardIosIcon style={{ fill: variable.greenLight }} />
                <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                    {param == 'newMaterial' ? t('addNewMaterial') : t('updateMaterial')}
                </Typography>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && material && (
                <MaterialContainer maxWidth={'md'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack padding={'0 1rem'} direction={'row'} justifyContent={'space-between'}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={material?.status == EStatus.Public} onChange={e => setMaterial({ ...material!, status: e.target.checked ? EStatus.Public : EStatus.Test })} />
                                }
                                label={t('public')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={material?.producer == EProducer.Maccaferri}
                                        onChange={e =>
                                            setMaterial({
                                                ...material!,
                                                producer: e.target.checked ? EProducer.Maccaferri : EProducer.Other,
                                                physicalData: e.target.checked ? material.physicalData : [material.physicalData[0]],
                                            })
                                        }
                                    />
                                }
                                label={t('maccaferriManufacturer')}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={material?.geotextileFilter} onChange={e => setMaterial({ ...material!, geotextileFilter: e.target.checked })} />}
                                label={t('geotextileFilter')}
                            />
                            {material?.producer == EProducer.Maccaferri && (
                                <FormControlLabel control={<Checkbox checked={material?.xties} onChange={e => setMaterial({ ...material!, xties: e.target.checked })} />} label={t('xTies')} />
                            )}
                            <MuiColorInput value={material?.color!} onChange={color => setMaterial({ ...material!, color })} />
                        </Stack>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'33%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {Object.keys(languages).map(x => (
                                        <Accordion disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{languages[x as TLanguages]}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack direction={'column'} spacing={2}>
                                                    <InputText
                                                        defaultValue={material!.translations!.find(y => y.iso == x)?.name}
                                                        id={x}
                                                        inputLabel={t('name')}
                                                        name={x}
                                                        customOnChange={handleChangeTraslation}
                                                    />
                                                    {x !== ELanguages.en && (
                                                        <Stack alignItems={'end'}>
                                                            <IconButton aria-label={`delete-material-${x}`} onClick={() => handleClickRemoveTranslation(x)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Stack>
                            <Stack width={'33%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {material.physicalData.map((x, i) => (
                                        <Accordion disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{t(`data ${i}`)}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <InputText
                                                    id={rockD50}
                                                    inputLabel={t(rockD50, {
                                                        unitOfMeasure: physicalData['rockD50']?.unitOfMeasure[EUnitOfMeasure.Metric],
                                                    })}
                                                    isNumber
                                                    name={rockD50}
                                                    customValue={material.physicalData[i].rockD50}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={rockD50in}
                                                    inputLabel={t(rockD50, {
                                                        unitOfMeasure: physicalData['rockD50']?.unitOfMeasure[EUnitOfMeasure.Imperial],
                                                    })}
                                                    isNumber
                                                    name={rockD50in}
                                                    customValue={material.physicalData[i].rockD50in}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={equivalentThickness}
                                                    inputLabel={t(equivalentThickness, {
                                                        unitOfMeasure: physicalData['equivalentThickness']?.unitOfMeasure[EUnitOfMeasure.Metric],
                                                    })}
                                                    isNumber
                                                    name={equivalentThickness}
                                                    customValue={material.physicalData[i].equivalentThickness}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={equivalentThicknessIn}
                                                    inputLabel={t(equivalentThickness, {
                                                        unitOfMeasure: physicalData['equivalentThickness']?.unitOfMeasure[EUnitOfMeasure.Imperial],
                                                    })}
                                                    isNumber
                                                    name={equivalentThicknessIn}
                                                    customValue={material.physicalData[i].equivalentThicknessIn}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={thickness}
                                                    inputLabel={t(thickness, {
                                                        unitOfMeasure: physicalData['thickness']?.unitOfMeasure[EUnitOfMeasure.Metric],
                                                    })}
                                                    isNumber
                                                    name={thickness}
                                                    customValue={material.physicalData[i].thickness}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={thicknessIn}
                                                    inputLabel={t(thickness, {
                                                        unitOfMeasure: physicalData['thickness']?.unitOfMeasure[EUnitOfMeasure.Imperial],
                                                    })}
                                                    isNumber
                                                    name={thicknessIn}
                                                    customValue={material.physicalData[i].thicknessIn}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={shieldsParameter}
                                                    inputLabel={t(shieldsParameter, {
                                                        unitOfMeasure: physicalData['shieldsParameter']?.unitOfMeasure[EUnitOfMeasure.Metric],
                                                    })}
                                                    isNumber
                                                    name={shieldsParameter}
                                                    customValue={material.physicalData[i].shieldsParameter}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                <InputText
                                                    id={relativeDensity}
                                                    inputLabel={t(relativeDensity, {
                                                        unitOfMeasure: physicalData['relativeDensity']?.unitOfMeasure[EUnitOfMeasure.Metric],
                                                    })}
                                                    isNumber
                                                    name={relativeDensity}
                                                    customValue={material.physicalData[i].relativeDensity}
                                                    customOnChange={(e:any) => handleChange(e, i)}
                                                />
                                                {material.physicalData.length > 1 && (
                                                    <IconButton
                                                        style={{ justifyContent: 'end', width: '100%' }}
                                                        aria-label='remove-image-material'
                                                        onClick={() => setMaterial({ ...material, physicalData: material.physicalData.filter((y: any, u: any) => u !== i) })}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                    {material.producer == EProducer.Maccaferri && (
                                        <IconButton
                                            style={{ justifyContent: 'end' }}
                                            aria-label='remove-image-material'
                                            onClick={() => setMaterial({ ...material, physicalData: [...material.physicalData, { ...newPhysicalData }] })}>
                                            <AddIcon />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Stack>
                            <Stack width={'33%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {image?.map((x: any, i: any) => {
                                        let children: React.ReactNode;
                                        if (x) {
                                            children = (
                                                <>
                                                    <Card variant='standard'>
                                                        <CardMedia height={160} component='img' image={typeof image[i] === 'string' ? image[i] : URL.createObjectURL(image[i])} alt='image-material' />
                                                    </Card>
                                                    <IconButton
                                                        style={{ justifyContent: 'end' }}
                                                        aria-label='remove-image-material'
                                                        onClick={() => (image.length > 1 ? setImage(image.filter((y: any, u: any) => u !== i)) : setImage(['']))}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            );
                                        } else {
                                            children = (
                                                <>
                                                    <Stack height={160} justifyContent={'center'} style={{ border: `0.1rem solid ${variable.greyLight}` }}>
                                                        <IconButton aria-label='add-image-material' onClick={() => fileInputRef.current!.click()}>
                                                            <UploadIcon />
                                                        </IconButton>
                                                        <input onChange={e => handleImageChange(e, i)} multiple={false} ref={fileInputRef} type='file' hidden />
                                                    </Stack>
                                                    {image.length > 1 && (
                                                        <IconButton
                                                            style={{ justifyContent: 'end' }}
                                                            aria-label='remove-image-material'
                                                            onClick={() => setImage(image.filter((y: any, u: any) => u !== i))}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </>
                                            );
                                        }
                                        return (
                                            <Accordion disableGutters>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                    <Typography>{t(`picture ${i}`)}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack direction={'column'} spacing={2}>
                                                        {children}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                    {image[0] !== '' && (
                                        <IconButton style={{ justifyContent: 'end' }} aria-label='remove-image-material' onClick={() => setImage([...image, ''])}>
                                            <AddIcon />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <Button variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </MaterialContainer>
            )}
        </>
    );
};

export default isAdmin(MaterialDetail);
